import { BUSINESS_LABEL } from "./businesses";
import { buildMetaSection } from "./templates";

// Generate 3 personalized prompts based on business type + answers + city + otherText
// Each prompt follows the ROL + CONTEXTO + TAREA + FORMATO + FILTRO structure.
// Returns: [{ title, text }, ...]

const common = (city) => `en ${city || "México"}`;

const formatFilter = `Formato: respuestas claras, en español mexicano casual (usa "tú"), en listas cuando apliquen, listas para copiar y pegar. Filtro: sin tecnicismos, sin publicidad pagada, sin inversión grande, ejecutable esta misma semana con lo que ya tengo.`;

function buildContext(parts) {
  return parts.filter(Boolean).join(". ");
}

// ---------- TAQUERÍA ----------
function promptsTaqueria(a, city) {
  const loc = common(city);
  const vol = a.volumen ? `Vendo ${a.volumen.toLowerCase()} tacos al día` : null;
  const noct = a.nocturno === "Sí" ? "Abro en horario nocturno" : a.nocturno === "No" ? "Solo abro durante el día" : null;
  const dom =
    a.domicilio === "Sí"
      ? "Ya ofrezco servicio a domicilio"
      : a.domicilio === "No"
      ? "No tengo servicio a domicilio"
      : a.domicilio === "Quiero ofrecerlo"
      ? "Quiero empezar a ofrecer servicio a domicilio"
      : null;
  const redes = a.redes ? `Redes sociales/Google Maps: ${a.redes.toLowerCase()}` : null;
  const reto = a.reto ? `Mi mayor reto es: ${a.reto.toLowerCase()}` : null;
  const comp = a.competencia ? `Competencia cercana: ${a.competencia.toLowerCase()}` : null;

  const contexto = buildContext([
    `Mi negocio es una taquería ${loc}`,
    vol,
    noct,
    dom,
    redes,
    reto,
    comp,
  ]);

  return [
    {
      title: "Estrategia para redes sociales",
      text: `Actúa como experto en marketing para taquerías en México. ${contexto}. Tarea: dame 5 ideas de publicaciones para Facebook e Instagram de esta semana que me ayuden con mi reto principal, incluyendo qué foto tomar, qué texto poner y qué hashtags usar. ${formatFilter}`,
    },
    {
      title: "Mensajes de WhatsApp para clientes",
      text: `Actúa como experto en marketing para taquerías en México. ${contexto}. Tarea: crea 3 mensajes cortos de WhatsApp para enviar a mis clientes frecuentes esta semana y que vuelvan a la taquería. ${formatFilter}`,
    },
    {
      title: "Promoción para atraer más clientes",
      text: `Actúa como experto en marketing para taquerías en México. ${contexto}. Tarea: diseña 2 promociones sencillas para mi taquería (una para entre semana y otra para fin de semana) que resuelvan directamente mi reto principal. Indica precio sugerido, cómo anunciarla y qué cuidar para no perder margen. ${formatFilter}`,
    },
  ];
}

// ---------- POLLERÍA ----------
function promptsPolleria(a, city) {
  const loc = common(city);
  const vol = a.volumen ? `Vendo ${a.volumen.toLowerCase()} pollos al día` : null;
  const dom =
    a.domicilio === "Sí"
      ? "Ofrezco servicio a domicilio"
      : a.domicilio === "A veces"
      ? "Doy servicio a domicilio solo a veces"
      : a.domicilio === "No"
      ? "No ofrezco servicio a domicilio"
      : null;
  const hm = a.horas_muertas && a.horas_muertas.startsWith("Sí")
    ? `Tengo horas muertas: ${a.horas_muertas.replace("Sí, ", "").toLowerCase()}`
    : a.horas_muertas === "No tengo"
    ? "No tengo horas muertas"
    : null;
  const combos =
    a.combos === "Solo piezas"
      ? "Vendo solo piezas sueltas"
      : a.combos === "Tengo combos"
      ? "Ya tengo combos armados"
      : a.combos === "Quiero crear combos"
      ? "Quiero crear combos nuevos"
      : null;
  const reto = a.reto ? `Mi mayor reto es: ${a.reto.toLowerCase()}` : null;
  const wa = a.whatsapp ? `Uso WhatsApp para pedidos: ${a.whatsapp.toLowerCase()}` : null;

  const contexto = buildContext([
    `Mi negocio es una pollería ${loc}`,
    vol,
    dom,
    hm,
    combos,
    reto,
    wa,
  ]);

  return [
    {
      title: "Mensajes de WhatsApp para activar clientes",
      text: `Actúa como experto en marketing para pollerías en México. ${contexto}. Tarea: crea 3 mensajes de WhatsApp para activar clientes y atacar directamente mi mayor reto. ${formatFilter}`,
    },
    {
      title: "Combos y promociones para subir el ticket",
      text: `Actúa como experto en marketing y menús para pollerías en México. ${contexto}. Tarea: propón 3 combos atractivos con precio sugerido y 1 promoción de "lleva 2 paga X" pensada para mi cliente. Incluye margen aproximado y cómo anunciarlos en el mostrador. ${formatFilter}`,
    },
    {
      title: "Estrategia para redes sociales esta semana",
      text: `Actúa como experto en marketing local para pollerías en México. ${contexto}. Tarea: dame 4 publicaciones listas para esta semana (Facebook e Instagram) con foto sugerida, texto y hashtags locales. ${formatFilter}`,
    },
  ];
}

// ---------- GYM FUNCIONAL ----------
function promptsGym(a, city) {
  const loc = common(city);
  const miem = a.miembros ? `Tengo ${a.miembros.toLowerCase()} miembros activos` : null;
  const clases = a.clases ? `Ofrezco: ${a.clases.toLowerCase()}` : null;
  const reto = a.reto ? `Mi reto principal es: ${a.reto.toLowerCase()}` : null;
  const redes = a.redes ? `Redes: ${a.redes.toLowerCase()}` : null;
  const prog = a.programa && a.programa !== "Ninguno" ? `Tengo programa especial para: ${a.programa.toLowerCase()}` : null;
  const adq = a.adquisicion ? `Mis nuevos miembros llegan por: ${a.adquisicion.toLowerCase()}` : null;

  const contexto = buildContext([
    `Mi negocio es un gimnasio de entrenamiento funcional ${loc}`,
    miem,
    clases,
    reto,
    redes,
    prog,
    adq,
  ]);

  return [
    {
      title: "Estrategia para Instagram y Facebook",
      text: `Actúa como experto en marketing para gimnasios funcionales en México. ${contexto}. Tarea: dame un calendario de 7 publicaciones para la próxima semana que me ayuden con mi reto principal, incluye tipo de contenido (reel, foto, historia), texto y CTA. ${formatFilter}`,
    },
    {
      title: "Retener miembros y evitar cancelaciones",
      text: `Actúa como experto en retención para gimnasios en México. ${contexto}. Tarea: dame 5 acciones concretas y de bajo costo para reducir cancelaciones este mes, incluyendo guion para hablar con un miembro que está por cancelar. ${formatFilter}`,
    },
    {
      title: "Atraer miembros nuevos en tu zona",
      text: `Actúa como experto en captación local para gimnasios funcionales en México. ${contexto}. Tarea: diseña una campaña de 14 días para atraer 10 miembros nuevos. Incluye oferta de entrada, guion para atender interesados y 3 ideas para hacer "clases abiertas" gratis. ${formatFilter}`,
    },
  ];
}

// ---------- TIENDA DE ROPA ----------
function promptsRopa(a, city) {
  const loc = common(city);
  const canal = a.canal ? `Vendo por canal: ${a.canal.toLowerCase()}` : null;
  const pub = a.publico ? `Mi público principal: ${a.publico.toLowerCase()}` : null;
  const reto = a.reto ? `Mi reto es: ${a.reto.toLowerCase()}` : null;
  const ig = a.instagram ? `Instagram: ${a.instagram.toLowerCase()}` : null;
  const temp = a.temporada ? `Temporada alta: ${a.temporada.toLowerCase()}` : null;
  const credito = a.credito ? `Apartado/crédito: ${a.credito.toLowerCase()}` : null;

  const contexto = buildContext([
    `Mi negocio es una tienda de ropa ${loc}`,
    canal,
    pub,
    reto,
    ig,
    temp,
    credito,
  ]);

  return [
    {
      title: "Estrategia para Instagram que venda",
      text: `Actúa como experto en marketing de moda para pymes en México. ${contexto}. Tarea: dame 7 ideas de reels y posts para esta semana que me ayuden con mi reto principal. Incluye guion corto, música sugerida y qué mostrar en cada uno. ${formatFilter}`,
    },
    {
      title: "Mover inventario estancado",
      text: `Actúa como experto en retail de moda en México. ${contexto}. Tarea: diseña 3 estrategias diferentes para mover el inventario que llevo meses sin vender, sin quemar precios. Incluye textos para publicar y cómo presentarlo en la tienda. ${formatFilter}`,
    },
    {
      title: "Fidelizar clientas que regresen",
      text: `Actúa como experta en fidelización de clientas para tiendas de ropa en México. ${contexto}. Tarea: crea un sistema simple de clientas frecuentes (sin apps ni sistemas caros) y 3 mensajes de WhatsApp para mantenerlas activas. ${formatFilter}`,
    },
  ];
}

// ---------- ABARROTES ----------
function promptsAbarrotes(a, city) {
  const loc = common(city);
  const cli = a.clientes ? `Recibo ${a.clientes.toLowerCase()} clientes al día` : null;
  const extras = a.extras ? `Además de abarrotes vendo: ${a.extras.toLowerCase()}` : null;
  const reto = a.reto ? `Mi mayor problema es: ${a.reto.toLowerCase()}` : null;
  const promos = a.promociones ? `Promociones: ${a.promociones.toLowerCase()}` : null;
  const credito = a.credito ? `Fío: ${a.credito.toLowerCase()}` : null;
  const wa = a.whatsapp ? `WhatsApp con clientes: ${a.whatsapp.toLowerCase()}` : null;

  const contexto = buildContext([
    `Mi negocio es una tienda de abarrotes de barrio ${loc}`,
    cli,
    extras,
    reto,
    promos,
    credito,
    wa,
  ]);

  return [
    {
      title: "Cómo competir contra Oxxo y tiendas grandes",
      text: `Actúa como experto en tiendas de barrio y abarrotes en México. ${contexto}. Tarea: dame 5 ventajas que puedo explotar contra una tienda grande y 3 acciones concretas para esta semana que hagan que el cliente me prefiera a mí. ${formatFilter}`,
    },
    {
      title: "Promociones y combos de barrio",
      text: `Actúa como experto en surtido y promociones para tiendas de abarrotes en México. ${contexto}. Tarea: arma 4 combos sencillos (ej. "combo desayuno", "combo botanero") con precio sugerido y margen aproximado, y diseña un cartel simple para el mostrador. ${formatFilter}`,
    },
    {
      title: "WhatsApp para pedidos y fidelización",
      text: `Actúa como experto en pequeños negocios de barrio en México. ${contexto}. Tarea: dame una estrategia paso a paso para empezar a tomar pedidos por WhatsApp, con 3 mensajes de bienvenida, catálogo sugerido y cómo avisar de ofertas del día. ${formatFilter}`,
    },
  ];
}

// ---------- BAR ----------
function promptsBar(a, city) {
  const loc = common(city);
  const horario = a.horario ? `Mi horario principal es ${a.horario.toLowerCase()}` : null;
  const reto = a.reto ? `Mi mayor reto es: ${a.reto.toLowerCase()}` : null;
  const eventosMap = {
    "Sí, frecuentemente": "organizo música en vivo o eventos especiales con frecuencia",
    "A veces": "a veces tengo música en vivo o eventos especiales",
    "No": "no tengo música en vivo ni eventos especiales",
  };
  const eventos = eventosMap[a.eventos];
  const adq = a.adquisicion ? `Mis clientes llegan por: ${a.adquisicion.toLowerCase()}` : null;
  const botanasMap = {
    "Solo bebidas": "solo vendo bebidas, sin botanas",
    "Botanas básicas": "ofrezco botanas básicas",
    "Menú completo de comida": "tengo menú completo de comida",
  };
  const botanas = botanasMap[a.botanas];
  const wa = a.whatsapp ? `Uso WhatsApp/redes para promociones: ${a.whatsapp.toLowerCase()}` : null;

  const contexto = buildContext([
    `Mi negocio es un bar ${loc}`,
    horario,
    reto,
    eventos,
    adq,
    botanas,
    wa,
  ]);

  return [
    {
      title: "Estrategia para atraer clientes a tu bar",
      text: `Actúa como experto en marketing para bares en México. ${contexto}. Tarea: dame un plan de 7 días con acciones concretas para atacar directamente mi reto principal, incluyendo qué hacer cada día de la semana y cómo invitar a clientes potenciales de la zona. ${formatFilter}`,
    },
    {
      title: "Ideas para subir el consumo por mesa",
      text: `Actúa como experto en ventas y menú para bares en México. ${contexto}. Tarea: dame 5 técnicas de upselling y 3 combos/promos listos para subir el ticket promedio por mesa sin verme agresivo. Incluye guion para el mesero y cómo presentar cada combo. ${formatFilter}`,
    },
    {
      title: "Contenido de redes y WhatsApp para promociones",
      text: `Actúa como experto en marketing local para bares en México. ${contexto}. Tarea: crea 5 publicaciones listas para subir esta semana (Instagram y Facebook) y 3 mensajes de WhatsApp para clientes frecuentes. Incluye foto sugerida, texto, hashtags locales y el mejor horario para publicar. ${formatFilter}`,
    },
  ];
}

// ---------- PUESTO DE COMIDA ----------
function promptsPuesto(a, city) {
  const loc = common(city);
  const productoRaw = a.producto || "comida";
  const producto =
    productoRaw === "Otro" ? "comida variada" : productoRaw.toLowerCase();
  const ubicacionMap = {
    "Mercado": "dentro de un mercado",
    "Calle fija": "en un punto fijo en la calle",
    "Tianguis": "en el tianguis",
    "Afuera de escuela u oficina": "afuera de una escuela u oficina",
  };
  const ubicacion = ubicacionMap[a.ubicacion];
  const cli = a.clientes ? `Atiendo ${a.clientes.toLowerCase()} clientes al día` : null;
  const hm =
    a.horas_muertas === "No tengo"
      ? "no tengo horas muertas"
      : a.horas_muertas
      ? `Tengo horas muertas: ${a.horas_muertas.toLowerCase()}`
      : null;
  const reto = a.reto ? `Mi mayor problema es: ${a.reto.toLowerCase()}` : null;
  const pedidosMap = {
    "Solo de paso": "todo es venta de paso, sin número para pedidos",
    "Tengo número fijo": "tengo número fijo para pedidos",
    "Acepto pedidos por WhatsApp": "acepto pedidos por WhatsApp",
  };
  const pedidos = pedidosMap[a.pedidos];

  const contexto = buildContext([
    `Mi negocio es un puesto de ${producto} ${loc}${ubicacion ? `, ubicado ${ubicacion}` : ""}`,
    cli,
    hm,
    reto,
    pedidos,
  ]);

  return [
    {
      title: "Estrategia para que me descubran más",
      text: `Actúa como experto en marketing para puestos de comida callejera en México. ${contexto}. Tarea: dame 5 acciones de muy bajo costo para que más gente de la zona descubra mi puesto esta semana, incluyendo letreros, volanteo inteligente y cómo aprovechar el boca a boca. ${formatFilter}`,
    },
    {
      title: "Promociones para horas muertas",
      text: `Actúa como experto en ventas para puestos de comida en México. ${contexto}. Tarea: diseña 2 promociones específicas para llenar mis horas muertas (si no tengo, para subir el ticket) y 3 frases de gancho para gritar o poner en el letrero que atraigan al cliente justo antes de pasar. ${formatFilter}`,
    },
    {
      title: "WhatsApp y redes para fidelizar",
      text: `Actúa como experto en marketing local para puestos de comida en México. ${contexto}. Tarea: dame una estrategia paso a paso para empezar a tomar pedidos o avisar del menú del día por WhatsApp, con 3 mensajes listos para enviar y 3 ideas de contenido sencillo para Facebook o TikTok hecho solo con mi celular. ${formatFilter}`,
    },
  ];
}


// ---------- VENTA POR REDES ----------
function promptsVentaRedes(a, city) {
  const loc = common(city);
  const productoRaw = a.producto || "productos";
  const producto =
    productoRaw === "Otro" ? "productos variados" : productoRaw.toLowerCase();
  const red = a.red_social || "redes sociales";
  const redLower =
    red === "Varias por igual" ? "varias redes sociales" : red;
  const seguidoresMap = {
    "Menos de 500": "menos de 500",
    "500 a 2,000": "entre 500 y 2,000",
    "2,000 a 10,000": "entre 2,000 y 10,000",
    "Más de 10,000": "más de 10,000",
  };
  const seguidores = seguidoresMap[a.seguidores] || "algunos";
  const problema = (a.reto || "convertir seguidores en clientes").toLowerCase();
  const catalogoMap = {
    "Tengo catálogo": "ya tengo un catálogo armado",
    "Publico uno por uno": "publico producto por producto sin catálogo",
    "No tengo nada organizado todavía": "aún no tengo nada organizado",
  };
  const catalogo = catalogoMap[a.catalogo];
  const tiempoMap = {
    "Apenas empiezo": "apenas empiezo",
    "Menos de 1 año": "menos de 1 año",
    "1 a 3 años": "entre 1 y 3 años",
    "Más de 3 años": "más de 3 años",
  };
  const tiempo = tiempoMap[a.tiempo] || "un tiempo";

  // PROMPT 1 — Contenido para vender más (plantilla exacta del usuario)
  const prompt1 = `Actúa como experto en ventas por redes sociales en México. Vendo ${producto} principalmente en ${redLower} ${loc} y tengo ${seguidores} seguidores. Mi mayor problema es ${problema}. Tarea: crea 3 publicaciones listas para subir que conviertan seguidores en compradores. Formato: texto del post + 3 hashtags locales + mejor horario para publicar. Tono: cercano, como vendedora de confianza, no corporativo. Filtro: sin palabras en inglés, sin publicidad pagada, ejecutable hoy.`;

  // PROMPT 2 — Cerrar ventas por WhatsApp (plantilla exacta del usuario)
  const prompt2 = `Actúa como experta en ventas directas por WhatsApp para vendedoras en México. Vendo ${producto} y llevo ${tiempo} en esto. Cuando alguien me pregunta precio, muchas veces no compra. Tarea: dame un guión de conversación para responder preguntas de precio y cerrar la venta sin parecer desesperada. Formato: conversación ejemplo con 5 intercambios, incluyendo cómo manejar "está muy caro" y "lo pienso". Filtro: tono amigable y natural, nada de scripts robóticos.`;

  // PROMPT 3 — Dinámico según el reto principal
  const extraCat = catalogo ? `, ${catalogo}` : "";
  let prompt3Title;
  let prompt3;
  switch (a.reto) {
    case "Conseguir más seguidores":
      prompt3Title = "Estrategia de crecimiento orgánico";
      prompt3 = `Actúa como experto en crecimiento orgánico en redes sociales para vendedoras en México. Vendo ${producto} en ${redLower} ${loc}, tengo ${seguidores} seguidores${extraCat} y llevo ${tiempo} en esto. Tarea: dame un plan de 14 días para aumentar mis seguidores de forma orgánica, con ideas de contenido día por día, colaboraciones locales y uso inteligente de hashtags. Formato: calendario día por día con tipo de publicación, texto base y objetivo. Filtro: sin publicidad pagada, sin comprar seguidores, ejecutable desde mi celular.`;
      break;
    case "Que los que me ven me compren":
      prompt3Title = "Convertir vistas en ventas";
      prompt3 = `Actúa como experto en conversión para ventas por redes sociales en México. Vendo ${producto} en ${redLower} ${loc}, tengo ${seguidores} seguidores${extraCat}. Muchos me ven pero pocos compran. Tarea: dame 5 llamadas a la acción (CTA) listas para usar y 3 mini‑guiones para contestar comentarios y DMs que lleven a cerrar la venta rápido. Formato: lista numerada con ejemplo listo para copiar. Filtro: tono cercano, sin presión, ejecutable hoy.`;
      break;
    case "Conseguir proveedores":
      prompt3Title = "Cómo encontrar y negociar con proveedores";
      prompt3 = `Actúa como experto en proveeduría para vendedoras por redes en México. Vendo ${producto}, llevo ${tiempo} en esto y estoy ${loc}. Tarea: dime dónde buscar proveedores confiables en México (mayoristas, importadores, centrales de abasto, ferias), cómo distinguir a los serios de los que estafan, y dame 3 mensajes listos para negociar precio y pedir muestra sin verme novata. Formato: pasos claros + mensajes listos para enviar por WhatsApp. Filtro: opciones reales en México, sin apps de dropshipping caras.`;
      break;
    case "Que me recomienden más":
      prompt3Title = "Programa simple de referidos";
      prompt3 = `Actúa como experto en marketing de referidos para vendedoras por redes en México. Vendo ${producto}, tengo ${seguidores} seguidores${extraCat} y estoy ${loc}. Tarea: diseña un programa de referidos muy sencillo (sin apps ni sistemas caros) para que mis clientas actuales me recomienden, con una recompensa justa que no me queme el margen. Formato: reglas del programa en 5 puntos + 3 mensajes listos para invitarlas a participar por WhatsApp. Filtro: fácil de administrar a mano, ejecutable esta semana.`;
      break;
    default:
      prompt3Title = "Estrategia personalizada esta semana";
      prompt3 = `Actúa como experto en ventas por redes sociales en México. Vendo ${producto} en ${redLower} ${loc} y tengo ${seguidores} seguidores. Tarea: dame 5 acciones concretas para mejorar mis ventas esta semana. Formato: lista con acción, tiempo estimado y cómo medir el resultado. ${formatFilter}`;
  }

  return [
    { title: "Contenido para vender más", text: prompt1 },
    { title: "Cerrar ventas por WhatsApp", text: prompt2 },
    { title: prompt3Title, text: prompt3 },
  ];
}

// ---------- TORTILLERÍA ----------
function promptsTortilleria(a, city) {
  const ciudad = (city && city.trim()) || "México";
  const kilosMap = {
    "Menos de 50kg": "menos de 50 kg",
    "50 a 150kg": "entre 50 y 150 kg",
    "Más de 150kg": "más de 150 kg",
  };
  const kilos = kilosMap[a.kilos] || "una cantidad importante";
  const clienteMap = {
    "Solo al menudeo (personas)": "personas en menudeo",
    "Solo mayoreo (taquerías, fondas)": "taquerías y fondas en mayoreo",
    "Ambos": "menudeo y mayoreo (a personas y a taquerías/fondas)",
  };
  const cliente = clienteMap[a.cliente] || "mis clientes";
  const diferenciadorMap = {
    "Maíz criollo o nixtamal propio": "uso maíz criollo y nixtamal propio",
    "Precio más bajo": "tengo el precio más bajo de la zona",
    "Mejor calidad": "ofrezco mejor calidad que la competencia",
    "Solo tortilla estándar": "vendo tortilla estándar (sin diferenciador especial)",
  };
  const diferenciador = diferenciadorMap[a.diferenciador] || "doy buen producto";
  const horarioMap = {
    "Mañana (6-10am)": "por la mañana, de 6 a 10am",
    "Mediodía (11am-2pm)": "al mediodía, de 11am a 2pm",
    "Todo el día parejo": "parejas todo el día",
  };
  const horario = horarioMap[a.horario] || "varían según el día";
  const reto = (a.reto || "conseguir más ventas").toLowerCase();

  const prompt1 = `Actúa como experto en ventas B2B para proveedores de alimentos en México con 20 años de experiencia trabajando con tortillerías locales. Mi tortillería en ${ciudad} produce ${kilos}/día y vendo principalmente a ${cliente}. Mi diferenciador es que ${diferenciador}. Mi mayor reto es ${reto}. Tarea: crea un guión de presentación de 2 minutos para cuando visito una taquería o restaurante a ofrecerles mi producto como proveedor. Incluye cómo responder si dicen "ya tengo proveedor". Formato: guión conversacional natural, frase de apertura, propuesta de valor, manejo de objeción, cierre con pedido de prueba. Tono: directo y confiable. Filtro: sin tecnicismos, sin que suene a vendedor agresivo, máximo 2 minutos al hablar.`;

  const prompt2 = `Actúa como experto en fidelización para negocios de alimentos básicos en México. Mi tortillería en ${ciudad} tiene clientes frecuentes en WhatsApp. Produzco ${kilos}/día y mi diferenciador es que ${diferenciador}. Tarea: crea 3 mensajes de WhatsApp para mandar a mis clientes actuales: uno para ofrecer pedido anticipado del día siguiente, uno para comunicar algo especial de mi producto, y uno para quincena con promoción especial. Formato: cada mensaje máximo 3 líneas, con emoji apropiado, tono como vecino de confianza. Listos para copiar y mandar. Filtro: sin inglés, sin que suene a empresa grande, que lo mande yo mismo.`;

  const prompt3 = `Actúa como experto en marketing para negocios de alimentos tradicionales en México. Mi tortillería en ${ciudad} tiene como diferenciador que ${diferenciador}. Mis horas pico son ${horario}. Tarea: crea una publicación para Facebook que explique por qué mi tortilla es mejor opción que la industrializada, y que invite a probarla esta semana. Formato: post de máximo 120 palabras, tono cercano y local, 3 hashtags de la ciudad, sugerencia de foto o imagen. Filtro: sin exagerar, sin prometer cosas falsas, que un cliente de 60 años lo entienda y le dé confianza.`;

  return [
    { title: "Guión para conseguir clientes mayoreo", text: prompt1 },
    { title: "Mensajes de WhatsApp para tus clientes", text: prompt2 },
    { title: "Publicación de Facebook para diferenciarte", text: prompt3 },
  ];
}

// ---------- ESTÉTICA / BARBERÍA ----------
function promptsEstetica(a, city) {
  const loc = common(city);
  const serviciosMap = {
    "Corte de cabello": "ofrezco principalmente corte de cabello",
    "Tinte y coloración": "me especializo en tinte y coloración",
    "Manicure y pedicure": "mi fuerte es manicure y pedicure",
    "Barba y afeitado": "soy barbero, corte de barba y afeitado",
    "Todos los anteriores": "ofrezco corte, tinte, manicure/pedicure y barba (servicio completo)",
  };
  const servicios = serviciosMap[a.servicios];
  const adq = a.adquisicion ? `Mis clientes nuevos llegan: ${a.adquisicion.toLowerCase()}` : null;
  const reto = a.reto ? `Mi mayor reto es: ${a.reto.toLowerCase()}` : null;
  const fotosMap = {
    "Sí, seguido": "publico seguido fotos de mis trabajos en redes",
    "A veces": "a veces publico fotos de mis trabajos",
    "Casi nunca": "casi nunca publico fotos de mis trabajos",
  };
  const fotos = fotosMap[a.fotos];
  const promoMap = {
    "Sí, tengo día de descuento": "ya tengo un día de descuento activo",
    "Sí, para clientes frecuentes": "tengo promoción para clientes frecuentes",
    "No tengo promoción": "no tengo ninguna promoción activa",
  };
  const promo = promoMap[a.promocion];
  const waMap = {
    "Sí, es mi canal principal": "uso WhatsApp como canal principal para agendar citas",
    "A veces": "uso WhatsApp a veces para citas",
    "No, todo es de paso": "no uso WhatsApp, todo es de paso",
  };
  const wa = waMap[a.whatsapp];

  const contexto = buildContext([
    `Mi negocio es una estética/barbería ${loc}`,
    servicios,
    adq,
    reto,
    fotos,
    promo,
    wa,
  ]);

  return [
    {
      title: "Estrategia para llenar la agenda",
      text: `Actúa como experto en marketing para estéticas y barberías en México. ${contexto}. Tarea: dame un plan de 7 días con acciones concretas para atacar directamente mi reto principal, incluyendo cómo activar horarios flojos y cómo aprovechar a mis clientes actuales para referir a alguien más. ${formatFilter}`,
    },
    {
      title: "Contenido de antes y después para redes",
      text: `Actúa como experto en marketing para estéticas y barberías en México. ${contexto}. Tarea: dame 5 ideas de publicaciones tipo "antes y después" para Instagram y Facebook de esta semana, con texto listo para copiar, hashtags locales y qué foto tomar en cada una. Incluye 2 ideas de reels cortos con celular. ${formatFilter}`,
    },
    {
      title: "Mensajes de WhatsApp para agendar y fidelizar",
      text: `Actúa como experto en fidelización para estéticas y barberías en México. ${contexto}. Tarea: crea 3 mensajes listos de WhatsApp: uno para recordarle la cita al cliente un día antes, uno para reactivar a quien no viene hace más de un mes, y uno para ofrecer un beneficio a mis clientes frecuentes. ${formatFilter}`,
    },
  ];
}

// ---------- CAFETERÍA ----------
function promptsCafeteria(a, city) {
  const loc = common(city);
  const producto = a.producto ? `Mi producto principal es: ${a.producto.toLowerCase()}` : null;
  const horario = a.horario_pico ? `Mi horario de mayor venta es: ${a.horario_pico.toLowerCase()}` : null;
  const llegada = a.llegada ? `Mis clientes nuevos llegan: ${a.llegada.toLowerCase()}` : null;
  const reto = a.reto ? `Mi mayor reto es: ${a.reto.toLowerCase()}` : null;
  const diferenciador = a.diferenciador ? `Mi diferenciador es: ${a.diferenciador.toLowerCase()}` : null;
  const redes = a.redes ? `Uso de redes: ${a.redes.toLowerCase()}` : null;

  const contexto = buildContext([
    `Mi negocio es una cafetería ${loc}`,
    producto,
    horario,
    llegada,
    reto,
    diferenciador,
    redes,
  ]);

  return [
    {
      title: "Estrategia para redes sociales",
      text: `Actúa como experto en marketing para cafeterías independientes en México. ${contexto}. Tarea: dame 3 ideas de publicaciones para Instagram y Facebook de esta semana que me ayuden con mi reto principal, incluyendo qué foto tomar, qué texto poner y qué hashtags usar. ${formatFilter}`,
    },
    {
      title: "Mensajes de WhatsApp para clientes",
      text: `Actúa como experto en marketing para cafeterías independientes en México. ${contexto}. Tarea: crea 3 mensajes cortos de WhatsApp para enviar a mis clientes frecuentes esta semana y que vuelvan a la cafetería. ${formatFilter}`,
    },
    {
      title: "Promoción para atraer más clientes",
      text: `Actúa como experto en marketing para cafeterías independientes en México. ${contexto}. Tarea: diseña 2 promociones sencillas para mi cafetería — una para entre semana y otra para fin de semana — que resuelvan directamente mi reto principal. Indica precio sugerido, cómo anunciarla y qué cuidar para no perder margen. ${formatFilter}`,
    },
  ];
}

// ---------- OTRO ----------
function promptsOtro(a, city, otherText) {
  const loc = common(city);
  const desc = a.descripcion || otherText || "un negocio pequeño";
  const cli = a.clientes ? `Recibo ${a.clientes.toLowerCase()} clientes al día` : null;
  const adq = a.adquisicion ? `Hoy consigo clientes por: ${a.adquisicion.toLowerCase()}` : null;
  const reto = a.reto ? `Mi mayor problema es: ${a.reto.toLowerCase()}` : null;
  const dig = a.digital ? `Presencia digital: ${a.digital.toLowerCase()}` : null;
  const mejora = a.mejora ? `Quiero mejorar primero: ${a.mejora.toLowerCase()}` : null;

  const contexto = buildContext([
    `Mi negocio ${loc} es: ${desc}`,
    cli,
    adq,
    reto,
    dig,
    mejora,
  ]);

  return [
    {
      title: "Estrategia para redes sociales",
      text: `Actúa como experto en marketing para pequeños negocios en México. ${contexto}. Tarea: dame 5 ideas de publicaciones para esta semana que me ayuden con mi reto principal, con texto listo para copiar y qué foto tomar en cada una. ${formatFilter}`,
    },
    {
      title: "Atraer más clientes esta semana",
      text: `Actúa como experto en marketing local para pymes en México. ${contexto}. Tarea: diseña un plan de 7 días con acciones concretas para atraer más clientes, usando mi canal principal actual. Incluye horarios, mensajes y cómo medir si funcionó. ${formatFilter}`,
    },
    {
      title: "Fidelización para que regresen",
      text: `Actúa como experto en fidelización para pequeños negocios en México. ${contexto}. Tarea: crea un sistema simple (sin apps, sin sistemas caros) para que mis clientes regresen, con 3 mensajes de WhatsApp listos para enviar esta semana. ${formatFilter}`,
    },
  ];
}

// The 6 allowed goals (from Screen 1 "¿Qué quieres lograr hoy?").
export const GOAL_OPTIONS = [
  "Vender más",
  "Conseguir clientes nuevos",
  "Publicar en redes sociales",
  "Responder mejor por WhatsApp",
  "Hacer una promoción",
  "Recuperar clientes que ya me compraron",
];

// Inject a goal-oriented directive right before the "Tarea:" section of every prompt
// so the LLM re-shapes its output toward the user's chosen objective for today.
function applyGoal(prompts, goal) {
  if (!goal) return prompts;
  const directive = `Mi objetivo principal para hoy es: ${goal.toLowerCase()}. Enfoca toda tu respuesta específicamente a ese objetivo y descarta ideas que no lo apoyen. `;
  return prompts.map((p) => ({
    ...p,
    text: p.text.replace(/(\s)Tarea:/, `$1${directive}Tarea:`),
  }));
}

// Inject the user-provided "extra" context (from the optional final question)
// right before the "Tarea:" section in every generated prompt.
function applyExtra(prompts, extra) {
  if (!extra || !extra.trim()) return prompts;
  const text = extra.trim().replace(/\s+/g, " ");
  const insertion = `Dato adicional del negocio: ${text}. `;
  return prompts.map((p) => ({
    ...p,
    text: p.text.replace(/(\s)Tarea:/, `$1${insertion}Tarea:`),
  }));
}

export function generatePrompts(businessType, answers, city, otherText, goal) {
  const extra = answers && typeof answers.extra === "string" ? answers.extra : "";
  let base;
  switch (businessType) {
    case "taqueria":
      base = promptsTaqueria(answers, city);
      break;
    case "polleria":
      base = promptsPolleria(answers, city);
      break;
    case "gym":
      base = promptsGym(answers, city);
      break;
    case "ropa":
      base = promptsRopa(answers, city);
      break;
    case "abarrotes":
      base = promptsAbarrotes(answers, city);
      break;
    case "venta_redes":
      base = promptsVentaRedes(answers, city);
      break;
    case "bar":
      base = promptsBar(answers, city);
      break;
    case "puesto":
      base = promptsPuesto(answers, city);
      break;
    case "tortilleria":
      base = promptsTortilleria(answers, city);
      break;
    case "estetica":
      base = promptsEstetica(answers, city);
      break;
    case "cafeteria":
      base = promptsCafeteria(answers, city);
      break;
    case "otro":
    default:
      base = promptsOtro(answers, city, otherText);
  }
  // Order: inject the extra business detail first (more permanent context),
  // then the goal directive (the "today's intent" layer), both before "Tarea:".
  // The financial META block is intentionally NOT prepended to the prompt text:
  // it used to leak into the clipboard copy and confused the LLM. It's still
  // available via buildMetaSection() if the UI ever needs to render it as a
  // visual motivation banner — but never inside the prompt itself.
  return applyGoal(applyExtra(base, extra), goal);
}

export function businessLabel(type) {
  return BUSINESS_LABEL[type] || "negocio";
}

// Rationale texts shown in the lightbulb modal on Screen 3.
// Each prompt is mapped to one of these 4 categories via categorizePrompt().
export const RATIONALE_TEXTS = {
  social:
    "Este prompt usa Prueba Social (Cialdini, 2001): cuando la gente ve que otros compraron, el cerebro interpreta que es seguro comprar. Tu post activa ese mecanismo.",
  whatsapp:
    "Basado en StoryBrand (Donald Miller): el cliente es el héroe, tú eres el guía. El mensaje habla del beneficio del cliente, no de tu negocio.",
  promo:
    "Usa Escasez y Urgencia (Ariely, 2008): las ofertas con límite de tiempo activan el instinto de no perder. Más efectivo que describir el producto.",
  retencion:
    "Basado en Hooked (Nir Eyal): el cliente que regresa lo hace por hábito, no por precio. Este mensaje crea el disparador emocional correcto.",
};

export const RATIONALE_LABEL = {
  social: "Prueba Social",
  whatsapp: "StoryBrand",
  promo: "Escasez y Urgencia",
  retencion: "Hooked",
};

// Map a prompt title to one of the 4 rationale categories.
// Order matters: retention keywords first (so "WhatsApp para fidelizar" → retencion),
// then promotion (so "Promociones..." → promo before "WhatsApp" matches), then whatsapp,
// and finally social as the default for "redes sociales / Instagram / Facebook / contenido".
export function categorizePrompt(title) {
  const t = (title || "").toLowerCase();
  if (/fideli|reten|regres|recupera|referid|cancel/.test(t)) return "retencion";
  if (/promoci|combo|hora muerta|atraer|descub|inventario|competir|consumo|upsell/.test(t)) return "promo";
  if (/whatsapp|cerrar|conver|guión|guion|cta|mensaj|proveedor/.test(t)) return "whatsapp";
  return "social";
}
